import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Pagination,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router";
import ChatGateway from "../../api/gateways/ChatGateway";
import Header from "../../components/header/Header";
import { Icon } from "../../modules/icon";
import { screens } from "../Navigation.types";

interface DocumentRow {
  [key: string]: string;
}

interface SignatureComparisonMetadata {
  title: string;
  period: string;
  generated: string;
  digitalPercentage: number;
  physicalPercentage: number;
  digitalCount: number;
  physicalCount: number;
  totalCount: number;
}

interface ParsedReport {
  metadata: SignatureComparisonMetadata;
  digitalSignatures: DocumentRow[];
  physicalSignatures: DocumentRow[];
  allRows: DocumentRow[];
}

enum SignatureType {
  ALL = "all",
  DIGITAL = "digital",
  PHYSICAL = "physical",
}

// ---------------------------------------------------------------------------
// Pie chart helpers — pure SVG, no external library
// ---------------------------------------------------------------------------
const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const slicePath = (cx: number, cy: number, r: number, startDeg: number, endDeg: number): string => {
  const span = endDeg - startDeg;
  if (span >= 359.99) {
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${(cx - 0.01).toFixed(2)} ${(cy - r).toFixed(2)} Z`;
  }
  const s = polarToCartesian(cx, cy, r, startDeg);
  const e = polarToCartesian(cx, cy, r, endDeg);
  return `M ${cx} ${cy} L ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${span > 180 ? 1 : 0} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)} Z`;
};

interface PieChartProps {
  digitalPct: number;
  physicalPct: number;
  digitalCount: number;
  physicalCount: number;
  totalCount: number;
}

const PieChart: React.FC<PieChartProps> = ({ digitalPct, physicalPct, digitalCount, physicalCount, totalCount }) => {
  const cx = 110, cy = 110, outerR = 90, holeR = 44;
  const digitalEnd = (digitalPct / 100) * 360;

  return (
    <div className="d-flex align-items-center justify-content-center gap-4 flex-wrap py-2">
      <svg width={220} height={220} viewBox="0 0 220 220" aria-label="Pie chart">
        {/* Digital slice */}
        <path d={slicePath(cx, cy, outerR, 0, digitalEnd)} fill="#6295ac" />
        {/* Physical slice */}
        <path d={slicePath(cx, cy, outerR, digitalEnd, 360)} fill="#ffc107" />
        {/* Donut hole */}
        <circle cx={cx} cy={cy} r={holeR} fill="white" />
        {/* Center label */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={14} fontWeight="bold" fill="#333">
          {totalCount.toLocaleString()}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize={11} fill="#666">
          ukupno
        </text>
      </svg>

      {/* Legend */}
      <div style={{ minWidth: 220 }}>
        <div className="d-flex align-items-center mb-3 gap-2">
          <div style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: "#6295ac", flexShrink: 0 }} />
          <div>
            <div><strong>Digital:</strong> {digitalPct}%</div>
            <div className="text-muted" style={{ fontSize: 13 }}>{digitalCount.toLocaleString()} dokumenata</div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: "#ffc107", flexShrink: 0 }} />
          <div>
            <div><strong>Physical:</strong> {physicalPct}%</div>
            <div className="text-muted" style={{ fontSize: 13 }}>{physicalCount.toLocaleString()} dokumenata</div>
          </div>
        </div>
      </div>
    </div>
  );
};
// ---------------------------------------------------------------------------

export function ChatSignatureComparisonReport({
  reportId,
}: {
  reportId: string;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ParsedReport | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [signatureType, setSignatureType] = useState<SignatureType>(
    SignatureType.ALL
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [chartView, setChartView] = useState<"bar" | "pie">("pie");
  const pageSize = 10;

  // Download CSV function with authentication
  const handleDownloadCSV = async () => {
    try {
      const csvContent = await ChatGateway.api.getSignatureComparisonReport(
        reportId
      );
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split("T")[0];
      link.setAttribute("href", url);
      link.setAttribute("download", `signature-comparison_${timestamp}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download CSV:", err);
      alert("Failed to download CSV file. Please try again.");
    }
  };

  // Fetch report
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await ChatGateway.api.getSignatureComparisonReport(
          reportId
        );
        const parsedReport = parseSignatureComparisonCSV(response);
        setReport(parsedReport);
      } catch (err: any) {
        setError(err.message || "Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  // Parse CSV content
  const parseSignatureComparisonCSV = (csvContent: string): ParsedReport => {
    const lines = csvContent
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    // Extract metadata from header comments
    const metadata: SignatureComparisonMetadata = {
      title: "",
      period: "",
      generated: "",
      digitalPercentage: 0,
      physicalPercentage: 0,
      digitalCount: 0,
      physicalCount: 0,
      totalCount: 0,
    };

    lines.forEach((line) => {
      if (line.startsWith("# Digital vs Physical"))
        metadata.title = line.substring(2);
      if (line.startsWith("# Period:"))
        metadata.period = line.substring(9).trim();
      if (line.startsWith("# Generated:"))
        metadata.generated = line.substring(12).trim();
      if (line.startsWith("# Digital Signatures:")) {
        const match = line.match(/(\d+\.?\d*)%\s*\((\d+)/);
        if (match) {
          metadata.digitalPercentage = parseFloat(match[1]);
          metadata.digitalCount = parseInt(match[2]);
        }
      }
      if (line.startsWith("# Physical Signatures:")) {
        const match = line.match(/(\d+\.?\d*)%\s*\((\d+)/);
        if (match) {
          metadata.physicalPercentage = parseFloat(match[1]);
          metadata.physicalCount = parseInt(match[2]);
        }
      }
      if (line.startsWith("# Total Documents:")) {
        metadata.totalCount = parseInt(line.substring(18).trim());
      }
    });

    // Find section boundaries
    const digitalSectionStart = lines.findIndex((line) =>
      line.startsWith("=== DIGITAL SIGNATURES")
    );
    const physicalSectionStart = lines.findIndex((line) =>
      line.startsWith("=== PHYSICAL SIGNATURES")
    );

    const parseSection = (start: number, end: number): DocumentRow[] => {
      if (start === -1) return [];
      const sectionLines = lines.slice(start, end);
      const headerLine = sectionLines.find(
        (line) => !line.startsWith("=") && !line.startsWith("#")
      );
      if (!headerLine) return [];
      const headers = parseCSVLine(headerLine);

      const rows: DocumentRow[] = [];
      let inSection = false;
      sectionLines.forEach((line) => {
        if (line === headerLine) {
          inSection = true;
          return;
        }
        if (inSection && !line.startsWith("=") && line.trim()) {
          const values = parseCSVLine(line);
          const row: DocumentRow = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          rows.push(row);
        }
      });
      return rows;
    };

    const digitalSignatures = parseSection(
      digitalSectionStart,
      physicalSectionStart
    );
    const physicalSignatures = parseSection(physicalSectionStart, lines.length);

    return {
      metadata,
      digitalSignatures,
      physicalSignatures,
      allRows: [...digitalSignatures, ...physicalSignatures],
    };
  };

  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        values.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    values.push(current);
    return values;
  };

  // Filter data
  const filteredData = useMemo(() => {
    if (!report) return [];
    let data = report.allRows;

    // Filter by signature type
    if (signatureType === SignatureType.DIGITAL) {
      data = report.digitalSignatures;
    } else if (signatureType === SignatureType.PHYSICAL) {
      data = report.physicalSignatures;
    }

    // Filter by search term
    if (searchTerm) {
      data = data.filter((row) =>
        Object.values(row).some((value) =>
          value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return data;
  }, [report, searchTerm, signatureType]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const tableHeaders =
    report?.allRows && report.allRows.length > 0
      ? Object.keys(report.allRows[0])
      : [];

  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading signature comparison report...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error || !report) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              <Alert.Heading>Error Loading Report</Alert.Heading>
              <p>{error || "The requested report could not be found."}</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <Header
        title={report.metadata.title || "Signature Comparison Report"}
        size="h5"
        actions={
          <>
            <Button
              variant="primary"
              className="me-2"
              onClick={handleDownloadCSV}
            >
              <Icon type="fa fa-download" />
              &nbsp; Download CSV
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const sessionId = window.location.pathname.match(
                  /\/chatbot\/sessionId\/([^/]+)/
                )?.[1];
                if (sessionId) {
                  navigate(`/chatbot/sessionId/${sessionId}`);
                } else {
                  navigate(screens.chatbot);
                }
              }}
            >
              <Icon type="fa fa-backward" />
              &nbsp; Back to conversation
            </Button>
          </>
        }
      />

      {/* Statistics Panel */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-1">
            <Card.Title className="mb-0">Comparative Statistics</Card.Title>
            <div className="btn-group" role="group" aria-label="Chart view toggle">
              <Button
                size="sm"
                variant={chartView === "bar" ? "primary" : "outline-primary"}
                onClick={() => setChartView("bar")}
              >
                <Icon type="fa fa-bar-chart" /> Bar
              </Button>
              <Button
                size="sm"
                variant={chartView === "pie" ? "primary" : "outline-primary"}
                onClick={() => setChartView("pie")}
              >
                <Icon type="fa fa-pie-chart" /> Pie
              </Button>
            </div>
          </div>

          <p className="text-muted mb-3">
            Period: {report.metadata.period} | Generated:{" "}
            {report.metadata.generated}
          </p>

          {chartView === "bar" ? (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Digital Signatures:</strong>{" "}
                    <Badge bg="info">{report.metadata.digitalPercentage}%</Badge> (
                    {report.metadata.digitalCount} documents)
                  </div>
                  <ProgressBar
                    now={report.metadata.digitalPercentage}
                    variant="info"
                    label={`${report.metadata.digitalPercentage}%`}
                  />
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Physical Signatures:</strong>{" "}
                    <Badge bg="warning">
                      {report.metadata.physicalPercentage}%
                    </Badge>{" "}
                    ({report.metadata.physicalCount} documents)
                  </div>
                  <ProgressBar
                    now={report.metadata.physicalPercentage}
                    variant="warning"
                    label={`${report.metadata.physicalPercentage}%`}
                  />
                </Col>
              </Row>
              <div className="text-center">
                <strong>Total Documents:</strong> {report.metadata.totalCount}
              </div>
            </>
          ) : (
            <PieChart
              digitalPct={report.metadata.digitalPercentage}
              physicalPct={report.metadata.physicalPercentage}
              digitalCount={report.metadata.digitalCount}
              physicalCount={report.metadata.physicalCount}
              totalCount={report.metadata.totalCount}
            />
          )}
        </Card.Body>
      </Card>

      {/* Filters Section */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search across all columns..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Signature Type</Form.Label>
            <Form.Select
              value={signatureType}
              onChange={(e) => {
                setSignatureType(e.target.value as SignatureType);
                setCurrentPage(1);
              }}
            >
              <option value={SignatureType.ALL}>All Signatures</option>
              <option value={SignatureType.DIGITAL}>Digital Only</option>
              <option value={SignatureType.PHYSICAL}>Physical Only</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            variant="secondary"
            onClick={() => {
              setSearchTerm("");
              setSignatureType(SignatureType.ALL);
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* Table Section */}
      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  {tableHeaders.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={tableHeaders.length} className="text-center">
                      No data found matching current filters
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor:
                          row.eSigned === "true" ? "#e3f2fd" : "#fff9e6",
                      }}
                    >
                      {tableHeaders.map((header) => (
                        <td key={header}>{row[header]}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Row className="mt-3">
              <Col className="d-flex justify-content-between align-items-center">
                <div>
                  Showing {(currentPage - 1) * pageSize + 1}-
                  {Math.min(currentPage * pageSize, filteredData.length)} of{" "}
                  {filteredData.length} results
                </div>
                <Pagination>
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => {
                      const prev = array[index - 1];
                      const showEllipsis = prev && page - prev > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <Pagination.Ellipsis disabled />}
                          <Pagination.Item
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        </React.Fragment>
                      );
                    })}
                  <Pagination.Next
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
